import { useState } from "react";
import { SlideOver } from "@/components/ui/slide-over";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Notify } from "@/components/ui/notify";
import type { CalibrationPendencyItem } from "@/types/closed-loop.types";
import { CAUSE_LABELS, PENDENCY_KIND_LABELS } from "./labels";
import { useResolvePendency } from "./hooks";

interface ResolvePendencyDialogProps {
  pendency: CalibrationPendencyItem | null;
  onOpenChange: (open: boolean) => void;
}

// Spec 077 (US3 / FR-024) — o ENCERRAMENTO da pendência, nos dois desfechos.
//
// A nota é obrigatória nos DOIS, e por razões diferentes: em "resolvida" ela é o que mudou no
// modelo — sem ela a iteração 4 não distingue calibração de arquivamento; em "descartada" ela é a
// prestação de contas de tirar trabalho da fila de outra pessoa.
//
// ⚠️ O DESCARTE passa por `Notify.confirm` antes de escrever: é irreversível (o desfecho é
// terminal) e tira da fila um trabalho que outra pessoa pode estar contando. Resolver não precisa —
// ali houve trabalho de verdade, e a fricção só atrapalharia quem já fez o que devia.

export function ResolvePendencyDialog({ pendency, onOpenChange }: ResolvePendencyDialogProps) {
  const [note, setNote] = useState("");
  const resolve = useResolvePendency();
  const canSubmit = note.trim().length > 0 && !resolve.isPending;

  function close() {
    setNote("");
    onOpenChange(false);
  }

  function submit(outcome: "RESOLVED" | "DISCARDED") {
    if (!pendency || !canSubmit) return;
    resolve.mutate(
      {
        id: pendency.id,
        appliedScenarioId: pendency.appliedScenarioId,
        outcome,
        note: note.trim(),
      },
      { onSuccess: close }
    );
  }

  async function discard() {
    const ok = await Notify.confirm({
      variant: "danger",
      title: "Descartar esta pendência?",
      description:
        "Descartar significa decidir que o modelo não será alterado por causa deste diagnóstico. O desfecho é definitivo e tira o item da fila de quem faria o trabalho — o motivo fica registrado com o seu nome.",
      confirmLabel: "Descartar pendência",
      cancelLabel: "Voltar",
    });
    if (ok) submit("DISCARDED");
  }

  return (
    <SlideOver
      open={!!pendency}
      onOpenChange={(open) => (open ? onOpenChange(true) : close())}
      title="Encerrar pendência de calibração"
      width={460}
    >
      <div className="space-y-4 p-5">
        {pendency ? (
          <div className="space-y-1 rounded-md border border-border-subtle bg-bg-muted-rd p-3">
            <p className="text-[13px] font-medium text-text-primary">
              {PENDENCY_KIND_LABELS[pendency.kind]}
            </p>
            <p className="text-[12px] text-text-secondary">
              {pendency.lineName} · origem: {CAUSE_LABELS[pendency.cause]}
            </p>
            {pendency.target ? (
              <p className="text-[12px] text-text-secondary">
                Alvo: {pendency.target.machineName} · {pendency.target.parameterName}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="resolution-note">O que foi feito (ou por que não será)</Label>
          <Input
            id="resolution-note"
            value={note}
            maxLength={1000}
            placeholder="Ex.: tempo de setup remedido em 12 min e atualizado no parâmetro"
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="text-[12px] text-text-secondary">
            Obrigatório nos dois desfechos. É esta frase que permitirá, meses depois, separar o que
            foi calibrado do que apenas saiu da fila.
          </p>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={close}>
            Voltar
          </Button>
          <Button variant="destructive" disabled={!canSubmit} onClick={() => void discard()}>
            Descartar
          </Button>
          <Button disabled={!canSubmit} onClick={() => submit("RESOLVED")}>
            {resolve.isPending ? "Encerrando…" : "Marcar como resolvida"}
          </Button>
        </div>
      </div>
    </SlideOver>
  );
}
